import {
  fetchBaseQuery,
  FetchBaseQueryError,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import { IStorageService } from "@shared/utils/IStorageService";
import { clearStoreTokens, setStoreTokens } from "@shared/utils/misc";
import pRetry from "p-retry";
import { getIsDemoQueryArgs } from "@shared/common/utils";
import { QueryReturnValue } from "@reduxjs/toolkit/query/react";
import { REQUESTS_AUTH_HEADER } from "@shared/common/constants";

const refreshToken = async () => {
  const refreshToken = await sharedLib.storageService.getRefreshToken();
  const { dispatch } = sharedLib.store;
  if (refreshToken) {
    return await dispatch(
      sharedLib.sharedApi.authentication.endpoints.refreshToken.initiate({
        refresh_token: refreshToken,
      })
    ).catch(() => {});
  }
};

export const refreshAndPersistToken = async (
  ignoreClear?: boolean
): Promise<boolean> => {
  let tokenUpdated = false;

  try {
    const response = await refreshToken();
    if (response && "data" in response) {
      const token = response.data?.token;
      setStoreTokens(token);
      tokenUpdated = true;
    } else if (response?.error?.status === 401 && !ignoreClear) {
      clearStoreTokens();
    }
  } catch (e) {}

  return tokenUpdated;
};

export const buildBaseQuery = (
  storageService: IStorageService<"web" | "mobile">,
  baseUrl: string,
  getUserAgent?: () => Promise<string>
) => {
  const getBaseQuery = (baseUrl: string) => {
    return fetchBaseQuery({
      baseUrl,
      prepareHeaders: async (headers) => {
        const token = await storageService.getAccessToken();
        if (token && !headers.get(REQUESTS_AUTH_HEADER)) {
          headers.set(REQUESTS_AUTH_HEADER, `token ${token}`);
        }
        if (getUserAgent) {
          headers.set("User-Agent", await getUserAgent());
        }
        return headers;
      },
    });
  };
  const userBaseQuery = getBaseQuery(baseUrl);
  const demoBaseQuery = getBaseQuery(baseUrl.replace("/app", "/demo"));

  const resultHasCode = (
    result:
      | QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
      | undefined,
    code: number
  ): boolean => {
    const error = result?.error;
    const errorStatus =
      error && "originalStatus" in error ? error.originalStatus : error?.status;
    return !!errorStatus && errorStatus === code;
  };

  const resultHasAuthError = (
    result:
      | QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
      | undefined
  ): boolean => resultHasCode(result, 401);

  const withRetry: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const runBaseQuery = async () => {
      const endpointUrl = typeof args === "string" ? args : args.url;
      let baseQuery = userBaseQuery;
      let newArgs = args;
      const isUserQueryArgs =
        typeof args !== "string" && args.body?.is_demo_query === false;
      const isDemoQueryArgs =
        typeof args !== "string" && getIsDemoQueryArgs(args.body, endpointUrl);
      if (!isUserQueryArgs && isDemoQueryArgs) {
        baseQuery = demoBaseQuery;
      }
      if (typeof args !== "string" && args.body) {
        const updatedBody = { ...args.body };
        delete updatedBody["is_demo_query"];
        newArgs = { ...args, body: updatedBody };
      }
      return baseQuery(newArgs, api, extraOptions);
    };

    const NUM_RETRIES = 3;

    return await pRetry(
      async (numRetry) => {
        let result = await runBaseQuery();
        if (resultHasAuthError(result)) {
          if (
            typeof args !== "string" &&
            args?.url?.includes("refresh_token/")
          ) {
            clearStoreTokens();
          } else if (await refreshAndPersistToken()) {
            result = await runBaseQuery();
          }
        } else if (
          typeof args !== "string" &&
          args.url?.startsWith("get_") &&
          result?.error?.status === "FETCH_ERROR" &&
          numRetry < NUM_RETRIES + 1
        ) {
          throw new Error();
        }
        return result;
      },
      {
        factor: 2,
        minTimeout: 500,
        retries: NUM_RETRIES,
      }
    );
  };

  return withRetry;
};
